import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';

const S = {
  page:  { minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:24, background:'#1a1c28' },
  card:  { maxWidth:420, width:'100%' },
  logo:  { textAlign:'center', marginBottom:32 },
  box:   { background:'#252838', borderRadius:20, padding:32, border:'1px solid #2e3150' },
  label: { display:'block', color:'#94a3b8', fontSize:13, fontWeight:600, marginBottom:6 },
  input: { width:'100%', padding:'12px 14px', background:'#12131f', border:'1px solid #1e2035', borderRadius:10, color:'#e2e8f0', fontSize:14, outline:'none', boxSizing:'border-box' },
  btn:   { width:'100%', padding:14, background:'linear-gradient(135deg,#f97316,#fb923c)', border:'none', borderRadius:12, color:'#fff', fontSize:16, fontWeight:800, cursor:'pointer', marginTop:4 },
  err:   { background:'#3b0a0a', color:'#f87171', padding:'10px 14px', borderRadius:8, marginBottom:16, fontSize:13 },
  ok:    { background:'#14532d', color:'#4ade80', padding:'10px 14px', borderRadius:8, marginBottom:16, fontSize:13 },
  muted: { color:'#64748b', fontSize:13 },
};

export default function AcceptInvite() {
  const [params]   = useSearchParams();
  const navigate   = useNavigate();
  const token      = params.get('token');

  const [invite,   setInvite]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [mode,     setMode]     = useState('signup'); // 'signup' | 'signin'
  const [name,     setName]     = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done,     setDone]     = useState(false);

  useEffect(() => {
    if (!token) { setError('No invite token found.'); setLoading(false); return; }
    fetch(`${process.env.REACT_APP_API_URL||'https://ivision-crm-backend-production.up.railway.app'}/api/rental-team/accept?token=${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) setError(data.error);
        else setInvite(data.invite);
        setLoading(false);
      })
      .catch(() => { setError('Could not load invite. Please try again.'); setLoading(false); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      let userId;

      if (mode === 'signup') {
        // Create new account
        const { data, error: signUpErr } = await supabase.auth.signUp({
          email: invite.email,
          password,
          options: { data: { full_name: name } },
        });
        if (signUpErr) throw signUpErr;
        userId = data.user?.id;
        // Wait for session
        await new Promise(r => setTimeout(r, 800));
      } else {
        // Sign in existing account
        const { data, error: signInErr } = await supabase.auth.signInWithPassword({
          email: invite.email,
          password,
        });
        if (signInErr) throw signInErr;
        userId = data.user?.id;
      }

      // Accept invite via backend (needs auth token)
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${process.env.REACT_APP_API_URL||'https://ivision-crm-backend-production.up.railway.app'}/api/rental-team/accept`,
        {
          method: 'POST',
          headers: { 'Content-Type':'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({ token }),
        }
      );
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Failed to accept invite');

      setDone(true);
      setTimeout(() => navigate('/app'), 2000);
    } catch(err) {
      setError(err.message || 'Something went wrong');
    }
    setSubmitting(false);
  };

  if (loading) return (
    <div style={S.page}>
      <div style={{ color:'#64748b', fontSize:14 }}>Loading invite...</div>
    </div>
  );

  if (done) return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.logo}>
          <div style={{ width:60,height:60,borderRadius:16,background:'linear-gradient(135deg,#f97316,#fb923c)',display:'inline-flex',alignItems:'center',justifyContent:'center',marginBottom:12 }}>
            <span style={{ fontSize:22,fontWeight:900,color:'#111' }}>iV</span>
          </div>
        </div>
        <div style={S.box}>
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>🎉</div>
            <div style={{ fontSize:20,fontWeight:800,color:'#e2e8f0',marginBottom:8 }}>You're in!</div>
            <div style={{ color:'#64748b',fontSize:14 }}>Redirecting you to {invite?.businessName}...</div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <div style={S.card}>
        {/* Logo */}
        <div style={S.logo}>
          <div style={{ width:60,height:60,borderRadius:16,background:'linear-gradient(135deg,#f97316,#fb923c)',display:'inline-flex',alignItems:'center',justifyContent:'center',marginBottom:12 }}>
            <span style={{ fontSize:22,fontWeight:900,color:'#111' }}>iV</span>
          </div>
          <div style={{ color:'#e2e8f0',fontWeight:800,fontSize:22 }}>iVision CRM</div>
        </div>

        <div style={S.box}>
          {error && !invite ? (
            <div>
              <div style={S.err}>⚠️ {error}</div>
              <p style={{ ...S.muted, textAlign:'center' }}>This invite may have expired or already been used.</p>
            </div>
          ) : invite ? (
            <>
              {/* Invite badge */}
              <div style={{ textAlign:'center', marginBottom:24 }}>
                <div style={{ fontSize:36,marginBottom:8 }}>👋</div>
                <div style={{ fontWeight:800,fontSize:18,color:'#e2e8f0',marginBottom:4 }}>
                  You've been invited to join
                </div>
                <div style={{ fontSize:20,fontWeight:900,color:'#f97316',marginBottom:8 }}>
                  {invite.businessName}
                </div>
                <div style={{ display:'inline-block',padding:'4px 12px',borderRadius:20,background:'#f9731622',color:'#f97316',fontSize:12,fontWeight:700 }}>
                  {invite.role} access
                </div>
              </div>

              <div style={{ padding:'10px 14px',background:'#12131f',borderRadius:8,marginBottom:20,fontSize:13,color:'#94a3b8',textAlign:'center' }}>
                Signing in as <strong style={{ color:'#e2e8f0' }}>{invite.email}</strong>
              </div>

              {error && <div style={S.err}>{error}</div>}

              {/* Mode toggle */}
              <div style={{ display:'flex',background:'#12131f',borderRadius:10,padding:4,marginBottom:20 }}>
                {['signup','signin'].map(m => (
                  <button key={m} type="button" onClick={() => { setMode(m); setError(''); }}
                    style={{ flex:1,padding:'8px 0',borderRadius:8,border:'none',background:mode===m?'#1e2035':'transparent',color:mode===m?'#e2e8f0':'#64748b',fontWeight:mode===m?700:400,fontSize:13,cursor:'pointer' }}>
                    {m === 'signup' ? 'Create Account' : 'Sign In'}
                  </button>
                ))}
              </div>

              <form onSubmit={submit}>
                {mode === 'signup' && (
                  <div style={{ marginBottom:14 }}>
                    <label style={S.label}>Your Name</label>
                    <input style={S.input} placeholder="Jane Smith" value={name} onChange={e=>setName(e.target.value)} required autoComplete="name" />
                  </div>
                )}
                <div style={{ marginBottom:20 }}>
                  <label style={S.label}>{mode === 'signup' ? 'Create Password' : 'Your Password'}</label>
                  <input style={S.input} type="password" placeholder={mode==='signup'?'At least 6 characters':'Your password'} value={password} onChange={e=>setPassword(e.target.value)} required autoComplete={mode==='signup'?'new-password':'current-password'} />
                </div>
                <button style={{ ...S.btn, opacity:submitting?0.6:1 }} disabled={submitting}>
                  {submitting ? 'Please wait...' : mode==='signup' ? 'Create Account & Join' : 'Sign In & Join'}
                </button>
              </form>

              <p style={{ ...S.muted, textAlign:'center', marginTop:16 }}>
                {mode === 'signup' ? 'Already have an account? ' : "Don't have an account? "}
                <button type="button" onClick={() => { setMode(mode==='signup'?'signin':'signup'); setError(''); }}
                  style={{ background:'none',border:'none',color:'#f97316',fontWeight:700,cursor:'pointer',fontSize:13 }}>
                  {mode === 'signup' ? 'Sign In instead' : 'Create one'}
                </button>
              </p>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
